import { useState, useMemo } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useAuth } from "@/contexts/AuthContext";
import { getAccountingPermissions } from "@/lib/accounting/engine";
import { VoucherType, VoucherLine } from "@/lib/accounting/types";
import { suggestLedger } from "@/lib/ai/engine";
import { DuplicateEntryWarning } from "@/components/accounting/DuplicateEntryWarning";
import { Button } from "@/components/ui/button";
import { FormModal } from "@/components/form/FormModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, AlertTriangle, CheckCircle2, Sparkles, Check, UserPlus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { QuickPartyModal } from "@/components/accounting/QuickPartyModal";

interface VoucherEntryFormProps {
  type: VoucherType;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
}

export function VoucherEntryForm({ type, trigger, onSuccess }: VoucherEntryFormProps) {
  const { ledgers, vouchers, createVoucher } = useAccounting();
  const { role } = useAuth();
  const { toast } = useToast();
  const perms = getAccountingPermissions(role, type);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [narration, setNarration] = useState("");
  const [lines, setLines] = useState<VoucherLine[]>([
    { id: crypto.randomUUID(), ledgerId: "", ledgerName: "", debit: 0, credit: 0 },
    { id: crypto.randomUUID(), ledgerId: "", ledgerName: "", debit: 0, credit: 0 },
  ]);

  // Quick Party Modal State
  const [quickPartyOpen, setQuickPartyOpen] = useState(false);
  const [quickPartyLineId, setQuickPartyLineId] = useState<string | null>(null);
  const [quickPartyRole, setQuickPartyRole] = useState<"customer" | "supplier" | "both">(
    type === "Receipt" ? "customer" : type === "Payment" ? "supplier" : "both"
  );

  const handleQuickPartyCreated = (party: any, ledgerId: string) => {
    if (quickPartyLineId) {
      updateLine(quickPartyLineId, "ledgerId", ledgerId);
    } else {
      const emptyIdx = lines.findIndex((l) => !l.ledgerId);
      if (emptyIdx !== -1) {
        updateLine(lines[emptyIdx].id, "ledgerId", ledgerId);
      } else {
        setLines((prev) => [
          ...prev,
          { id: crypto.randomUUID(), ledgerId, ledgerName: party.name, debit: 0, credit: 0 },
        ]);
      }
    }
  };

  const totalDebit = lines.reduce((s, l) => s + l.debit, 0);
  const totalCredit = lines.reduce((s, l) => s + l.credit, 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  const addLine = () => {
    setLines([...lines, { id: crypto.randomUUID(), ledgerId: "", ledgerName: "", debit: 0, credit: 0 }]);
  };

  const removeLine = (id: string) => {
    if (lines.length <= 2) return;
    setLines(lines.filter((l) => l.id !== id));
  };

  const updateLine = (id: string, field: keyof VoucherLine, value: string | number) => {
    setLines(lines.map((l) => {
      if (l.id !== id) return l;
      if (field === "ledgerId") {
        const ledger = ledgers.find((lg) => lg.id === value);
        return { ...l, ledgerId: value as string, ledgerName: ledger?.name || "" };
      }
      return { ...l, [field]: value };
    }));
  };

  const handleSubmit = async () => {
    const invalidLines = lines.filter((l) => !l.ledgerId);
    if (invalidLines.length > 0) {
      toast({ title: "Error", description: "All lines must have a ledger selected", variant: "destructive" });
      return;
    }

    const result = await createVoucher(type, lines, narration, undefined, undefined, 0, 0, 0, 0, undefined, { date });
    if (result.success) {
      toast({ title: "Voucher Created", description: `${result.voucher?.voucherNumber} created successfully` });
      setOpen(false);
      setNarration("");
      setLines([
        { id: crypto.randomUUID(), ledgerId: "", ledgerName: "", debit: 0, credit: 0 },
        { id: crypto.randomUUID(), ledgerId: "", ledgerName: "", debit: 0, credit: 0 },
      ]);
      onSuccess?.();
    } else {
      toast({ title: "Validation Error", description: result.error, variant: "destructive" });
    }
  };

  if (!perms.canCreate) return null;

  return (
    <>
    <FormModal
      open={open}
      onOpenChange={setOpen}
      size="transaction"
      trigger={
        trigger || (
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-sm">
            <Plus className="h-4 w-4 mr-2" /> New {type} Voucher
          </Button>
        )
      }
      icon={<Sparkles className="h-5 w-5 text-primary" />}
      title={`New ${type} Voucher`}
      description="Double-entry journal posting with balanced debits & credits, AI smart ledger suggestions, and instant reconciliation."
      footerLeft={
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(false)}
          className="text-xs h-9"
        >
          Cancel <span className="text-[10px] text-muted-foreground ml-1">Esc</span>
        </Button>
      }
      footerRight={
        <Button
          onClick={handleSubmit}
          disabled={!isBalanced}
          className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-9 font-semibold gap-1.5 shadow-sm"
        >
          Create Voucher
        </Button>
      }
    >
      <div className="space-y-4">
          {/* Voucher Date & Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-md bg-muted/50 border border-border">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1 flex items-center justify-between">
                <span>Voucher Date *</span>
                {date !== new Date().toISOString().split("T")[0] && (
                  <span className="text-[10px] text-amber-500 font-semibold">Backdated Entry</span>
                )}
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-8 w-full px-2.5 text-xs rounded border border-border bg-card font-mono outline-none focus:ring-1 focus:ring-accent"
                required
              />
            </div>
            <div className="text-xs text-muted-foreground flex flex-col justify-center">
              <strong className="text-foreground">Double-Entry Rule:</strong>
              <span>Every debit must have an equal credit. Total Debit = Total Credit.</span>
            </div>
          </div>

          {/* Narration with AI Ledger Detection — moved to top for AI suggestion flow */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5">Narration / Description</label>
            <textarea
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent resize-none"
              rows={2}
              placeholder='e.g. "Payment to Amazon for office supplies" — AI will suggest the correct ledger'
              maxLength={500}
            />
            <AILedgerSuggestionBar
              narration={narration}
              ledgers={ledgers}
              lines={lines}
              onApply={(ledgerId, ledgerName) => {
                const emptyIdx = lines.findIndex(l => !l.ledgerId);
                if (emptyIdx !== -1) {
                  updateLine(lines[emptyIdx].id, "ledgerId", ledgerId);
                } else {
                  const newLine: VoucherLine = { id: crypto.randomUUID(), ledgerId, ledgerName, debit: 0, credit: 0 };
                  setLines([...lines, newLine]);
                }
                toast({ title: "Ledger Applied", description: `AI suggested "${ledgerName}" — applied to entry line` });
              }}
            />
          </div>

          {/* Voucher Lines */}
          <div className="space-y-3">
            <div className="flex items-center justify-between pb-1 border-b border-border/40">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Entries</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setQuickPartyLineId(null);
                  setQuickPartyRole(type === "Receipt" ? "customer" : type === "Payment" ? "supplier" : "both");
                  setQuickPartyOpen(true);
                }}
                className="h-7 text-xs gap-1.5 border-dashed text-primary hover:bg-primary/5"
              >
                <UserPlus className="h-3.5 w-3.5" />
                <span>+ Quick Add Customer / Supplier</span>
              </Button>
            </div>

            <div className="hidden md:grid grid-cols-[1fr_120px_120px_40px] gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
              <span>Ledger</span>
              <span className="text-right">Debit (₹)</span>
              <span className="text-right">Credit (₹)</span>
              <span></span>
            </div>

            {lines.map((line, idx) => (
              <div key={line.id}>
                {/* Desktop row */}
                <div className="hidden md:grid grid-cols-[1fr_120px_120px_40px] gap-2 items-center">
                  <Select value={line.ledgerId} onValueChange={(v) => updateLine(line.id, "ledgerId", v)}>
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Select ledger..." />
                    </SelectTrigger>
                    <SelectContent>
                      <div
                        className="px-2 py-1.5 text-xs text-primary font-medium hover:bg-primary/10 rounded cursor-pointer flex items-center gap-1.5 border-b border-border/60 mb-1"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setQuickPartyLineId(line.id);
                          setQuickPartyRole(type === "Receipt" ? "customer" : type === "Payment" ? "supplier" : "both");
                          setQuickPartyOpen(true);
                        }}
                      >
                        <UserPlus className="h-3.5 w-3.5 text-primary" />
                        <span>+ Add New Customer / Supplier...</span>
                      </div>
                      {ledgers.map((l) => (
                        <SelectItem key={l.id} value={l.id} className="text-sm">
                          <span>{l.name}</span>
                          <span className="text-muted-foreground ml-2 text-xs">({l.group})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input
                    type="number" min="0" step="0.01"
                    value={line.debit || ""}
                    onChange={(e) => updateLine(line.id, "debit", parseFloat(e.target.value) || 0)}
                    className="h-9 px-2 text-sm text-right rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent"
                    placeholder="0.00"
                  />
                  <input
                    type="number" min="0" step="0.01"
                    value={line.credit || ""}
                    onChange={(e) => updateLine(line.id, "credit", parseFloat(e.target.value) || 0)}
                    className="h-9 px-2 text-sm text-right rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent"
                    placeholder="0.00"
                  />
                  <button onClick={() => removeLine(line.id)} className="text-muted-foreground hover:text-destructive transition-colors" disabled={lines.length <= 2}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Mobile card */}
                <div className="md:hidden mobile-form-card space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Line {idx + 1}</span>
                    <button onClick={() => removeLine(line.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1" disabled={lines.length <= 2}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <Select value={line.ledgerId} onValueChange={(v) => updateLine(line.id, "ledgerId", v)}>
                    <SelectTrigger className="h-10 text-sm">
                      <SelectValue placeholder="Select ledger..." />
                    </SelectTrigger>
                    <SelectContent>
                      <div
                        className="px-2 py-1.5 text-xs text-primary font-medium hover:bg-primary/10 rounded cursor-pointer flex items-center gap-1.5 border-b border-border/60 mb-1"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setQuickPartyLineId(line.id);
                          setQuickPartyRole(type === "Receipt" ? "customer" : type === "Payment" ? "supplier" : "both");
                          setQuickPartyOpen(true);
                        }}
                      >
                        <UserPlus className="h-3.5 w-3.5 text-primary" />
                        <span>+ Add New Customer / Supplier...</span>
                      </div>
                      {ledgers.map((l) => (
                        <SelectItem key={l.id} value={l.id} className="text-sm">
                          <span>{l.name}</span>
                          <span className="text-muted-foreground ml-2 text-xs">({l.group})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-muted-foreground mb-1 block">Debit (₹)</label>
                      <input
                        type="number" min="0" step="0.01" inputMode="decimal"
                        value={line.debit || ""}
                        onChange={(e) => updateLine(line.id, "debit", parseFloat(e.target.value) || 0)}
                        className="h-10 w-full px-3 text-sm text-right rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground mb-1 block">Credit (₹)</label>
                      <input
                        type="number" min="0" step="0.01" inputMode="decimal"
                        value={line.credit || ""}
                        onChange={(e) => updateLine(line.id, "credit", parseFloat(e.target.value) || 0)}
                        className="h-10 w-full px-3 text-sm text-right rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button onClick={addLine} className="text-xs text-accent hover:underline flex items-center gap-1 mt-1 touch-target">
              <Plus className="h-3 w-3" /> Add Line
            </button>
          </div>

          {/* Totals */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2 border-t border-border px-1">
            <span className="text-sm font-semibold text-foreground">Totals</span>
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-foreground">Dr: ₹{totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              <span className="text-sm font-bold text-foreground">Cr: ₹{totalCredit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              {isBalanced ? (
                <CheckCircle2 className="h-4 w-4 text-success" />
              ) : totalDebit > 0 || totalCredit > 0 ? (
                <AlertTriangle className="h-4 w-4 text-warning" />
              ) : null}
            </div>
          </div>

          {/* AI Duplicate Detection */}
          <DuplicateEntryWarning
            vouchers={vouchers}
            type={type}
            totalAmount={totalDebit}
            narration={narration}
            date={new Date().toISOString().split("T")[0]}
          />

        </div>
    </FormModal>

    {/* Quick Party Modal */}
    <QuickPartyModal
      open={quickPartyOpen}
      onOpenChange={setQuickPartyOpen}
      partyType={quickPartyRole}
      onPartyCreated={handleQuickPartyCreated}
    />
    </>
  );
}

// AI Ledger Suggestion Bar — appears below narration when AI detects a ledger match
function AILedgerSuggestionBar({ narration, ledgers, lines, onApply }: {
  narration: string;
  ledgers: { id: string; name: string; group: string }[];
  lines: VoucherLine[];
  onApply: (ledgerId: string, ledgerName: string) => void;
}) {
  const suggestion = useMemo(() => suggestLedger(narration), [narration]);
  if (!suggestion || suggestion.confidence < 0.4) return null;

  const matchedLedger = ledgers.find(l =>
    l.group === suggestion.group || l.name.toLowerCase().includes(suggestion.ledgerName.toLowerCase())
  );
  const isHigh = suggestion.confidence > 0.8;
  const alreadyApplied = matchedLedger && lines.some(l => l.ledgerId === matchedLedger.id);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        className={`mt-2 flex items-start gap-2.5 p-3 rounded-lg border ${isHigh ? "bg-success/5 border-success/20" : "bg-accent/5 border-accent/20"}`}
      >
        <Sparkles className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isHigh ? "text-success" : "text-accent"}`} />
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">AI suggests ledger:</span>
            <span className="text-xs font-bold text-foreground">{suggestion.ledgerName}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isHigh ? "bg-success/10 text-success" : "bg-accent/10 text-accent"}`}>
              {(suggestion.confidence * 100).toFixed(0)}%
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">{suggestion.explanation}</p>
          <p className="text-[10px] text-muted-foreground/70">Group: {suggestion.group}</p>
        </div>
        {matchedLedger && !alreadyApplied ? (
          <button
            onClick={() => onApply(matchedLedger.id, matchedLedger.name)}
            className={`flex items-center gap-1 text-[11px] font-medium px-3 py-1.5 rounded-md transition-colors whitespace-nowrap ${isHigh ? "bg-success/10 text-success hover:bg-success/20" : "bg-accent/10 text-accent hover:bg-accent/20"}`}
          >
            <Check className="h-3 w-3" /> Apply
          </button>
        ) : alreadyApplied ? (
          <span className="text-[10px] text-success font-medium flex items-center gap-1 whitespace-nowrap">
            <CheckCircle2 className="h-3 w-3" /> Applied
          </span>
        ) : (
          <span className="text-[10px] text-muted-foreground italic whitespace-nowrap">No matching ledger</span>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
